import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { rem, rgba } from 'polished';
import { colors, mixins } from '../../core/styles/theme/default';
import { useEscape } from '../../core/utils/Hooks';

const StyledDialog = styled.dialog`
  max-width: calc(100dvw - ${rem(32)});
  max-height: calc(100dvh - ${rem(32)});
  width: ${rem(600)};
  padding: 0;
  border: none;
  background: ${colors.white};
  border-radius: ${rem(2)};

  &::backdrop {
    background: ${rgba(colors.nightBlue700, 0.5)};
  }

  ${({ theme }) => theme?.stylesExtension?.modal?.dialog}
`;

const StyledDialogContent = styled.div<{ hasCloseBtn: boolean }>`
  position: relative;
  color: ${colors.black};

  ${({ hasCloseBtn }) =>
    mixins.responsiveStyles({
      padding: {
        zero: hasCloseBtn ? `${rem(44)} ${rem(16)}` : 16,
        medium: hasCloseBtn ? `${rem(44)} ${rem(32)}` : 32,
      },
    })}
`;

const StyledDialogCloseBtn = styled.button`
  &,
  &:focus {
    position: absolute;
  }

  top: 0;
  right: 0;
  width: ${rem(44)};
  height: ${rem(44)};
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;

  &:before,
  &:after {
    content: '';
    width: ${rem(22)};
    height: ${rem(2)};
    position: absolute;
    top: 50%;
    left: 50%;
    background: ${colors.primary};
    transform: translate(-50%, -50%) rotate(45deg);
  }

  &:after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }
`;

const Modal = ({
  isOpen,
  hasCloseBtn = true,
  onClose,
  children,
}: ModalProps) => {
  const [isModalOpen, setModalOpen] = useState(isOpen);
  const ref = useRef<HTMLDialogElement | null>(null);

  const handleCloseModal = () => {
    onClose?.();
    setModalOpen(false);
  };

  useEscape(() => {
    handleCloseModal();
  });

  useEffect(() => {
    setModalOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (isModalOpen) {
      ref.current?.showModal();
    } else {
      ref.current?.close();
    }
  }, [isModalOpen]);

  return (
    <StyledDialog
      ref={ref}
      onClick={(e) => {
        if ((e.target as HTMLElement).tagName === 'DIALOG') handleCloseModal();
      }}
      role="dialog"
      aria-modal="true"
    >
      <StyledDialogContent hasCloseBtn={hasCloseBtn}>
        {hasCloseBtn && (
          <StyledDialogCloseBtn
            onClick={handleCloseModal}
            aria-label="Close modal"
          />
        )}
        {children}
      </StyledDialogContent>
    </StyledDialog>
  );
};

export type ModalProps = {
  isOpen: boolean;
  hasCloseBtn?: boolean;
  onClose?: () => void;
  children: React.ReactNode;
};

export default Modal;
